/**
 * Custom hook for managing contractual charges
 * Uses React Query for data fetching and mutations
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBudgetQueries } from 'utils/api/queries';
import { useBudgetMutation } from 'utils/api/mutations';
import { toast } from 'react-toastify';
import { toastStyle } from 'utils/helper';

export const useContractualCharges = (budgetId, enabled = true) => {
  const queryClient = useQueryClient();
  // Use React Query for fetching contractual charges
  const { data: queryData, refetch } =
    useBudgetQueries.useContractualChargesQuery({
      enabled: enabled && Boolean(budgetId),
      budgetId,
    });

  // Get charges from query data
  const charges = queryData?.data || [];

  // Sort by position
  const sortedCharges = [...charges].sort((a, b) => {
    if (a.position !== b.position) {
      return (a.position || 0) - (b.position || 0);
    }
    return (a.id || 0) - (b.id || 0);
  });

  // Create mutation hooks
  const createMutation = useBudgetMutation.useCreateContractualChargeMutation();
  const updateMutation = useBudgetMutation.useUpdateContractualChargeMutation();
  const deleteMutation = useBudgetMutation.useDeleteContractualChargeMutation();
  const repositionMutation =
    useBudgetMutation.useRepositionContractualChargesMutation();

  // Create charge with duplicate prevention
  const createCharge = useCallback(
    async payload => {
      // Prevent duplicate calls if mutation is already in progress
      if (createMutation.isLoading || createMutation.isPending) {
        return;
      }

      try {
        const result = await createMutation.mutateAsync(payload);
        // Invalidate and refetch the query to update the list
        // This ensures we get the latest data from the server
        await refetch();
        return result;
      } catch (error) {
        toast.error('Failed to create contractual charge', toastStyle);
        throw error;
      }
    },
    [createMutation, refetch]
  );

  // Update charge
  const updateCharge = useCallback(
    async payload => {
      try {
        const result = await updateMutation.mutateAsync(payload);
        refetch();
        return result;
      } catch (error) {
        toast.error('Failed to update contractual charge', toastStyle);
        throw error;
      }
    },
    [updateMutation, refetch]
  );

  // Delete charge
  const deleteCharge = useCallback(
    async id => {
      try {
        await deleteMutation.mutateAsync(id);
        refetch();
      } catch (error) {
        toast.error('Failed to delete contractual charge', toastStyle);
        throw error;
      }
    },
    [deleteMutation, refetch]
  );

  // Reposition charges with optimistic update
  const repositionCharges = useCallback(
    async reorderedCharges => {
      const queryKey = ['get-contractual-charges', budgetId];
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueryData(queryKey);

      // Get current query data structure
      const currentData = queryClient.getQueryData(queryKey) || queryData;

      // Optimistically update the cache with new order
      const optimisticCharges = reorderedCharges.map((charge, index) => ({
        ...charge,
        position: index,
      }));

      // Update the cache immediately with optimistic data
      queryClient.setQueryData(queryKey, {
        ...currentData,
        data: optimisticCharges,
      });

      try {
        const payload = reorderedCharges.map((charge, index) => ({
          id: charge.id,
          position: index,
        }));
        await repositionMutation.mutateAsync({ contractual_charges: payload });
        // Refetch to ensure we have the latest data from server
        await refetch();
      } catch (error) {
        // Rollback to previous value on error
        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        }
        toast.error('Failed to reorder contractual charges', toastStyle);
        throw error;
      }
    },
    [budgetId, repositionMutation, refetch, queryClient, queryData]
  );

  return {
    contractualCharges: sortedCharges,
    loading:
      createMutation.isLoading ||
      updateMutation.isLoading ||
      deleteMutation.isLoading,
    createCharge,
    updateCharge,
    deleteCharge,
    repositionCharges,
    refetch,
  };
};
